import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'modules/main/redux';
import { useToggle, useAppDispatch } from 'modules/main';
import { RegularXs } from 'modules/ui/typography';
import {
  Button,
  Checkbox,
  ColorIndicator,
  Chips,
  InnerCollapsibleContainer,
  TooltipInfoIcon,
  Paywall,
} from 'modules/ui/components';
import {
  isRoutingDataOnSelector,
  turnOnShowRoutingDataFlag,
} from 'modules/metadata';
import { RoutingLayers, RoutingColors } from 'modules/mapLayers/consts';
import {
  clearRoutingIsochrones,
  toggleRoutingIsochrones,
  toogleActiveRoutings,
} from 'modules/routing/redux';
import { RoutingType } from 'modules/routing/consts';
import { formatPolarSpreadTooltip } from 'modules/routing/utils/tooltip';
import { formatModelName } from 'modules/routing/utils/formater';
import { useDownloadRoutingCsv } from '../../hooks';
import {
  activeIsochronesStorageKeySelector,
  downloadedRoutingByIdSelector,
} from '../../redux/selectors';
import { DownloadedRoute } from '../../types';
import { getRoutingDetailsLabel } from '../../utils/labels';

import { ButtonsContainer, ButtonsLinksContainer } from './styledComponents';
import { Plan } from 'modules/subscription/types';

type Props = {
  id: string;
  index: number;
  handleSourceChange: (
    routing: DownloadedRoute,
    index: number,
  ) => Promise<void>;
  changeLayerVisibility: (hide: boolean, layerName: string) => void;
  clearSources: (index: number) => void;
  setRoutingPlotId: (id: string | null) => void;
};

const ItemUi: React.FC<Props> = ({
  id,
  index,
  handleSourceChange,
  changeLayerVisibility,
  clearSources,
  setRoutingPlotId,
}) => {
  const dispatch = useAppDispatch();
  const [isOpened, toggleIsOpened] = useToggle(false);
  const isRoutingDataOn = useSelector(isRoutingDataOnSelector);
  const [isRouteDisplayed, toggleIsRouteDisplayed, setIsRouteDisplayed] =
    useToggle(false);
  const [isPolarSpreadDisplayed, toggleIsPolarSpreadDisplayed] =
    useToggle(false);
  const routing = useSelector((state: RootState) =>
    downloadedRoutingByIdSelector(state, id),
  );

  const tooltipMessage = useMemo(() => {
    return formatPolarSpreadTooltip(
      routing?.metadata?.Statistics?.startTime,
      routing?.polarSpreadMetadata,
    );
  }, [routing]);

  const activeIsochronesStorageKey = useSelector(
    activeIsochronesStorageKeySelector,
  );

  const toggleIsochrones = useCallback(() => {
    dispatch(toggleRoutingIsochrones(routing?.izochronesStorageId || ''));
    setIsRouteDisplayed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routing?.izochronesStorageId, dispatch]);

  useEffect(() => {
    return (): void => {
      dispatch(clearRoutingIsochrones(routing?.izochronesStorageId || ''));
      clearSources(index);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openMeteogram = useCallback(() => {
    window.open(`/meteogram/routing/${id}`, '_blank');
  }, [id]);

  const openRoutingTable = useCallback(() => {
    window.open(`/table/${id}`, '_blank');
  }, [id]);

  const { handleDownloadCsv, csvDownloading } = useDownloadRoutingCsv(id);

  useEffect(() => {
    if (
      isRouteDisplayed &&
      activeIsochronesStorageKey !== routing?.izochronesStorageId
    ) {
      dispatch(toggleRoutingIsochrones(routing?.izochronesStorageId || ''));
    }
    if (
      !isRouteDisplayed &&
      activeIsochronesStorageKey === routing?.izochronesStorageId
    ) {
      dispatch(toggleRoutingIsochrones(routing?.izochronesStorageId || ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRouteDisplayed]);

  const toggleRouting = useCallback(() => {
    toggleIsRouteDisplayed();
    if (routing?.id) {
      dispatch(toogleActiveRoutings(routing?.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routing?.id]);

  useEffect(() => {
    if (routing) {
      handleSourceChange(routing, index);
    }
  }, [routing, handleSourceChange, index]);

  useEffect(() => {
    changeLayerVisibility(
      !(isRoutingDataOn && isRouteDisplayed),
      `${RoutingLayers.TimedOptimalRouteBarbs}${index}`,
    );
    changeLayerVisibility(
      !(isRoutingDataOn && isRouteDisplayed),
      `${RoutingLayers.TimedOptimalRoute}${index}`,
    );
    changeLayerVisibility(
      !(isRoutingDataOn && isRouteDisplayed),
      `${RoutingLayers.OptimalRoute}${index}`,
    );
    changeLayerVisibility(
      !(isRoutingDataOn && isRouteDisplayed),
      `${RoutingLayers.DashedOptimalRoute}${index}`,
    );
    changeLayerVisibility(
      !(isRoutingDataOn && isRouteDisplayed),
      `${RoutingLayers.RoutingTimedLocation}${index}`,
    );
  }, [isRouteDisplayed, isRoutingDataOn, changeLayerVisibility, index]);

  useEffect(() => {
    changeLayerVisibility(
      !(isRoutingDataOn && isPolarSpreadDisplayed),
      `${RoutingLayers.RoutingPolarSpreadLine}${index}`,
    );
    changeLayerVisibility(
      !(isRoutingDataOn && isPolarSpreadDisplayed),
      `${RoutingLayers.RoutingPolarSpreadPoints}${index}`,
    );
    changeLayerVisibility(
      !(isRoutingDataOn && isPolarSpreadDisplayed),
      `${RoutingLayers.RoutingPolarSpreadTimedLocation}${index}`,
    );
  }, [isPolarSpreadDisplayed, isRoutingDataOn, changeLayerVisibility, index]);

  useEffect(() => {
    if (isRouteDisplayed || isPolarSpreadDisplayed) {
      dispatch(turnOnShowRoutingDataFlag());
    }
  }, [dispatch, isRouteDisplayed, isPolarSpreadDisplayed]);

  useEffect(() => {
    if (!isRouteDisplayed && isPolarSpreadDisplayed) {
      toggleIsRouteDisplayed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolarSpreadDisplayed]);

  useEffect(() => {
    if (
      isRouteDisplayed &&
      !isPolarSpreadDisplayed &&
      routing?.hasPolarSpread
    ) {
      toggleIsPolarSpreadDisplayed();
    }
    if (
      !isRouteDisplayed &&
      isPolarSpreadDisplayed &&
      routing?.hasPolarSpread
    ) {
      toggleIsPolarSpreadDisplayed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRouteDisplayed]);

  const openEnsembleStatsModal = useCallback(() => {
    setRoutingPlotId(routing?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRoutingPlotId, routing?.id]);

  const isEps =
    routing?.metadata?.Meteo?.Type?.toLowerCase() === RoutingType.eps;
  const isFc = routing?.metadata?.Meteo?.Type?.toLowerCase() === RoutingType.fc;

  if (!routing) return null;

  return (
    <InnerCollapsibleContainer
      isOpened={isOpened}
      toggleIsOpened={toggleIsOpened}
      title={`${
        routing.metadata?.Internal?.routingName || `Route ID: ${routing.id}`
      } (${formatModelName(
        routing.metadata?.Meteo?.Model,
        routing.metadata?.Meteo?.Type.toLowerCase(),
      )}${isEps ? ' EPS' : ''})`}
      titleComponent={<ColorIndicator color={RoutingColors[index]} />}
      titleExtraComponent={
        <TooltipInfoIcon text={getRoutingDetailsLabel(routing)} />
      }
    >
      <ButtonsContainer>
        <Checkbox
          value={isRouteDisplayed}
          label={formatModelName(
            routing.metadata?.Meteo?.Model,
            routing.metadata?.Meteo?.Type.toLowerCase(),
          )}
          name={formatModelName(
            routing.metadata?.Meteo?.Model,
            routing.metadata?.Meteo?.Type.toLowerCase(),
          )}
          isSecondary={false}
          onChange={toggleRouting}
        />
        <ButtonsLinksContainer>
          {isFc && (
            <>
              <Button
                isSmall
                isSecondary
                label="Table"
                clickHandler={openRoutingTable}
              />
              {!!routing.meteogramStorageId && (
                <Button
                  isSmall
                  isSecondary
                  label="Meteogram"
                  clickHandler={openMeteogram}
                />
              )}
              <Paywall placement="right" minimumPlan={Plan.Unlimited}>
                <Button
                  isLoading={csvDownloading}
                  isSmall
                  isSecondary
                  label="CSV"
                  clickHandler={handleDownloadCsv}
                />
              </Paywall>
            </>
          )}
          {isEps && !!routing.allRoutingStorageId && (
            <Button
              isSmall
              isSecondary
              label="Stats"
              clickHandler={openEnsembleStatsModal}
            />
          )}
        </ButtonsLinksContainer>
      </ButtonsContainer>
      {isRouteDisplayed && isEps && routing.timedOptimalRouteStorageId && (
        <RegularXs style={{ marginBottom: 8 }}>
          Click on each route to view their isochrones, wind barbs, and points.
        </RegularXs>
      )}
      <div>
        {!!routing.izochronesStorageId && isFc && (
          <>
            <Chips
              label="Isochrones"
              onClick={toggleIsochrones}
              name={routing.izochronesStorageId || ''}
              value={routing.izochronesStorageId === activeIsochronesStorageKey}
            />
          </>
        )}
        {!!routing.izochronesStorageId && isEps && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Chips
              label="Isochrones"
              onClick={toggleIsochrones}
              name={routing.izochronesStorageId || ''}
              value={routing.izochronesStorageId === activeIsochronesStorageKey}
            />
          </div>
        )}
        {routing.hasPolarSpread && (
          <Chips
            tooltipText={tooltipMessage}
            label="Polar spread"
            onClick={toggleIsPolarSpreadDisplayed}
            name={routing.polarSpreadLinesStorageId || ''}
            value={isPolarSpreadDisplayed}
          />
        )}
      </div>
    </InnerCollapsibleContainer>
  );
};

export default React.memo(ItemUi);
